import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import {
  Mutation,
  PspPaymentMethodsInput,
  PaymentChannel,
} from "src/types/graphql.d";
import { PspCompany } from "src/types/psp.d";
import { CartInfo } from "src/types/cartInfo";

export interface PspPaymentMethodsData {
  pspPaymentMethods: Mutation["pspPaymentMethods"];
}
interface PspPaymentMethodsVars {
  data: PspPaymentMethodsInput;
}

interface PspPaymentMethodsHook {
  data?: PspPaymentMethodsData | null;
  loading: boolean;
  error?: ApolloError;
  pspPaymentMethods: (dataVariable: {
    psp: PspCompany;
    applepay: boolean;
    cartInfo: CartInfo;
  }) => void;
}

const PSP_PAYMENT_CREATE_TRANSACTION = gql`
  mutation pspPaymentMethods($data: PspPaymentMethodsInput!) {
    pspPaymentMethods(data: $data) {
      payment_id
      payment_methods {
        id
        description
        image
      }
    }
  }
`;

const usePspPaymentMethods = ({
  refetchQueries = [],
  onCompleted,
}: {
  refetchQueries?: string[];
  onCompleted?: (pspPaymentMethodsData: PspPaymentMethodsData) => void;
} = {}): PspPaymentMethodsHook => {
  const [pspPaymentMethodsMutation, { data, loading, error }] = useMutation<
    PspPaymentMethodsData,
    PspPaymentMethodsVars
  >(PSP_PAYMENT_CREATE_TRANSACTION, {
    refetchQueries,
    onCompleted,
  });

  const pspPaymentMethods: PspPaymentMethodsHook["pspPaymentMethods"] =
    useCallback(
      ({ applepay, cartInfo, psp }) => {
        const includeWallets = [];
        if (applepay) {
          includeWallets.push("applepay");
        }

        void pspPaymentMethodsMutation({
          variables: {
            data: {
              psp,
              include_wallets: includeWallets.join(","),
              cart_id: cartInfo.id,
              cart_version: cartInfo.version,
              channel: PaymentChannel.Web,
            },
          },
        });
      },
      [pspPaymentMethodsMutation]
    );

  return { pspPaymentMethods, data, loading, error };
};

export { usePspPaymentMethods };
